// Keeps "Mic on" on (iPhone): when you turn the Mic switch on, the mic stays on until you turn it off, or until
// you haven't talked for 3 minutes (and aren't on a call).
//
// What was turning it off: after ~10-30 s untouched the iPhone dims and locks the screen, iOS pauses the app and
// closes its mic. So while the mic is on (or a call is up) the screen is kept awake, and if iOS closes the mic
// anyway (you locked the phone, switched apps), it's opened again as soon as Joy Phone is back on screen.

import { isIOS, micKeepReady, setMicKeepReady, micOn, micOff, micIsOn, micStream, onMicClosedByPhone, onMicReadyChange, micOpensSilently } from "./devices";

/** Minutes of no talking before the mic turns itself off. */
export const MIC_IDLE_MS = 3 * 60_000;
/** Talking: louder than about -50 dBFS (room hiss on an iPhone mic sits near -70). */
const TALK_DB = -50;

let inCall: () => boolean = () => false;
let onAutoOff: () => void = () => {};
let lastTalk = Date.now();

const wantMic = () => isIOS() && micKeepReady();
const visible = () => document.visibilityState === "visible";

// ---- Keep the screen awake ------------------------------------------------------------------------
type Sentinel = { released: boolean; release(): Promise<void>; addEventListener(t: "release", fn: () => void): void };
let lock: Sentinel | null = null;
let asking = false;
let video: HTMLVideoElement | null = null;
// 2 s of black video with no sound track at all (a sound track, even silent, made iOS show a lock-screen
// "now playing" card). Playing it inline keeps iOS from locking the screen where the Wake Lock
// API isn't kept (home-screen apps on iOS before 18.4).
const AWAKE_MP4 = "data:video/mp4;base64,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";

async function holdScreen() {
  if (!visible()) return;
  // iPhone home-screen apps before iOS 18.4 accept the wake lock but don't keep it; the video covers those.
  if (isIOS()) playAwakeVideo();
  const wl = (navigator as Navigator & { wakeLock?: { request(t: "screen"): Promise<Sentinel> } }).wakeLock;
  if (wl && !lock && !asking) {
    asking = true;
    try {
      lock = await wl.request("screen");
      lock.addEventListener("release", () => {
        lock = null;
      });
    } catch {
      lock = null;
    } finally {
      asking = false;
    }
  }
  if (!isIOS()) {
    if (!lock && !asking) playAwakeVideo();
    else stopAwakeVideo();
  }
}

function playAwakeVideo() {
  if (!video) {
    video = document.createElement("video");
    video.setAttribute("playsinline", "");
    video.setAttribute("muted", "");
    video.muted = true;
    video.loop = true;
    video.src = AWAKE_MP4;
    video.setAttribute("aria-hidden", "true");
    video.style.cssText = "position:fixed;width:1px;height:1px;opacity:0.01;left:0;bottom:0;pointer-events:none;z-index:-1";
    document.body.append(video);
  }
  if (video.paused) void video.play().catch(() => {});
}
function stopAwakeVideo() {
  if (video) {
    video.pause();
    video.remove();
    video = null;
  }
}

function releaseScreen() {
  const l = lock;
  lock = null;
  void l?.release().catch(() => {});
  stopAwakeVideo();
}

// ---- Listen for talking (on this phone only; nothing is sent) --------------------------------------
let ctx: AudioContext | null = null;
let analyser: AnalyserNode | null = null;
let listening: MediaStream | null = null;
let listenTrack: MediaStreamTrack | null = null;
const buf = new Float32Array(1024);

function listenTo(s: MediaStream | null) {
  if (s === listening) return;
  stopListening();
  const t = s?.getAudioTracks()[0];
  if (!s || !t) return;
  try {
    // The "mic on" track is muted (disabled); a copy of it, enabled, gives the level.
    listenTrack = t.clone();
    listenTrack.enabled = true;
    ctx ??= new AudioContext();
    if (ctx.state === "suspended") void ctx.resume().catch(() => {});
    const src = ctx.createMediaStreamSource(new MediaStream([listenTrack]));
    analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    src.connect(analyser);
    listening = s;
  } catch {
    stopListening();
  }
}
function stopListening() {
  listenTrack?.stop();
  listenTrack = null;
  analyser?.disconnect();
  analyser = null;
  listening = null;
  // Closed, not left running: a running audio engine is what puts the "now playing" card on the lock screen.
  void ctx?.close().catch(() => {});
  ctx = null;
}

/** True: talking. Null: can't hear right now (iOS wants a tap before the level reader runs). */
function heardTalking(): boolean | null {
  if (!analyser || !ctx) return null;
  if (ctx.state !== "running") {
    void ctx.resume().catch(() => {});
    return null;
  }
  analyser.getFloatTimeDomainData(buf);
  let sum = 0;
  for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
  return 20 * Math.log10(Math.sqrt(sum / buf.length) || 1e-8) > TALK_DB;
}

// ---- The loop ------------------------------------------------------------------------------------
let reopening = false;
let visibleSince = Date.now();
/**
 * iPhone closed the mic because you left the app (or locked the screen), and it's coming straight back: the switch
 * keeps showing on meanwhile, instead of flicking off and on each time you switch apps.
 */
export function micComingBack(): boolean {
  return wantMic() && !micIsOn() && micOpensSilently() && (!visible() || reopening || Date.now() - visibleSince < 4000);
}
async function reopen() {
  // Never by itself any more. When iPhone closes the mic (you switched apps), reopening it made iPhone's mic sound
  // every time you came back, and could even ask for permission. If Joy Phone is allowed the mic, nothing needs to
  // stay open (calls open it without asking); if not, the switch shows off and you turn it on when you want.
  reopening = false;
}

/** Runs every half second while the app is open (timers pause with the app; that's fine). */
function tick() {
  const calling = inCall();
  const micWanted = wantMic();
  // During a call, and whenever the level can't be read, it counts as talking: never off by mistake.
  if (calling || !micWanted || heardTalking() !== false) lastTalk = Date.now();
  if (micWanted && !calling && Date.now() - lastTalk > MIC_IDLE_MS) {
    // 3 minutes without talking: the mic turns itself off.
    setMicKeepReady(false);
    micOff();
    stopListening();
    onAutoOff();
  }
  if (wantMic() && !micIsOn()) void reopen();
  listenTo(wantMic() && !calling && visible() ? micStream() : null);
  if ((wantMic() && micIsOn()) || (calling && isIOS())) void holdScreen();
  else releaseScreen();
}

/**
 * Starts the keeper. `isInCall`: a call is up (the mic never turns itself off during a call, and the screen
 * stays awake). `autoOff`: told when the mic turned itself off after 3 quiet minutes.
 */
export function startMicGuard(isInCall: () => boolean, autoOff: () => void) {
  inCall = isInCall;
  onAutoOff = autoOff;
  setInterval(tick, 500);
  document.addEventListener("visibilitychange", () => {
    if (!visible()) {
      // Off screen: nothing of ours keeps playing (iOS releases the wake lock and may close the mic; both come back below).
      stopListening();
      stopAwakeVideo();
      return;
    }
    visibleSince = Date.now();
    if (wantMic()) lastTalk = Math.max(lastTalk, Date.now() - MIC_IDLE_MS + 30_000); // a moment to start talking
    tick();
  });
  // iOS closed the mic: open it again right away if we're on screen (else when we're back).
  onMicClosedByPhone(() => setTimeout(() => void reopen(), 300));
  // Turning the switch on starts the 3 minutes over.
  let was = wantMic();
  onMicReadyChange(() => {
    const now = wantMic();
    if (now && !was) lastTalk = Date.now();
    was = now;
  });
  document.addEventListener("pointerdown", () => {
    void reopen();
    if (ctx?.state === "suspended") void ctx.resume().catch(() => {});
  }, { capture: true, passive: true });
}
